/**
 * Which module a ROUTE belongs to (frontend gate, design §3b).
 *
 * A route document may carry an explicit `module`; when it does not, the
 * module is derived from the route's `routeFeature` through the registry's
 * feature-key map — so every existing route is covered without a data
 * migration. A route whose feature key belongs to no module (system/public
 * plumbing) is a platform route and passes the module gate.
 */
import { MODULE_FEATURE_KEYS, MODULE_KEYS, ModuleKey } from './module-switch';

const byFeatureKey: ReadonlyMap<string, ModuleKey> = (() => {
    const m = new Map<string, ModuleKey>();
    for (const key of MODULE_KEYS) {
        for (const fk of MODULE_FEATURE_KEYS[key]) if (!m.has(fk)) m.set(fk, key);
    }
    return m;
})();

/** The module a per-user feature key grants, or undefined for a key no module claims. */
export function moduleForFeatureKey(featureKey: string | undefined | null): ModuleKey | undefined {
    return featureKey ? byFeatureKey.get(featureKey) : undefined;
}

export interface RouteLike {
    module?: string;
    routeFeature?: string;
    /** A section-level route shared by several modules (e.g. the Management Tools dashboard). */
    systemRoute?: boolean;
    features?: string[];
}

/**
 * Explicit `module` wins (validated against the registry); otherwise derive
 * from `routeFeature`. Undefined = platform route, not gated by the switchboard.
 */
export function moduleForRoute(route: RouteLike | null | undefined): ModuleKey | undefined {
    if (!route) return undefined;
    if (route.module && (MODULE_KEYS as readonly string[]).includes(route.module)) return route.module as ModuleKey;
    return moduleForFeatureKey(route.routeFeature);
}

/**
 * Is a route's module gate open, given a per-module check (`isOn` = the switchboard's three tiers for this user)?
 *
 * A normal route follows the one module from `moduleForRoute`. A SYSTEM route belongs to a whole section — the
 * Management Tools dashboard lists checklist, client-abc, management-tools and statistics — and before the
 * switchboard it was shown to anyone holding ANY of those features. Gating it on its `routeFeature` module alone
 * hid the dashboard from every rep with only Statistics/Client ABC (prod, 2026-10-27), so a system route is open
 * when ANY module among its features is on. An explicit `module` field still decides alone. No module anywhere =
 * platform route = open.
 */
export function isRouteModuleOn(route: RouteLike | null | undefined, isOn: (module: ModuleKey) => boolean): boolean {
    if (!route) return true;
    const own = moduleForRoute(route);
    if (!route.systemRoute || (route.module && own)) return own ? isOn(own) : true;
    const mods = new Set<ModuleKey>();
    if (own) mods.add(own);
    for (const f of route.features ?? []) {
        const m = moduleForFeatureKey(f);
        if (m) mods.add(m);
    }
    return mods.size === 0 || [...mods].some(isOn);
}
